import { Component, inject, OnInit, Output, EventEmitter, HostListener, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbActiveModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { FuelReconciliationService } from '../../../services/fuel-reconciliation.service';
import { Subject } from 'rxjs';
import { FuelSlipUploadPreference, UpsertFuelSlipUploadPreferenceRequest, CurrencyOption } from '../../../models/fuel-recon.model';
import { ToastrService } from 'ngx-toastr';
import * as ct from "countries-and-timezones";
import { CommonModule } from '@angular/common';
import { dateTimeCultures } from '../../../../../_shared/constants/date-time-culture.constant';
import { getAllTimezones, toMinutes, toSeconds } from '../../../helpers/timezone.helper';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { CountryFlagComponent } from '../../../../../_shared/components/country-flag/country-flag.component';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-upload-preference-modal',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, CommonModule, CountryFlagComponent, NgbTooltipModule, TranslocoDirective, TranslocoPipe],
  templateUrl: './upload-preference-modal.component.html',
  styleUrl: './upload-preference-modal.component.scss',
})
export class UploadPreferenceModalComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  @Output() upsertSuccess = new EventEmitter<void>();
  @ViewChild('currencySearchInput') currencySearchInput?: ElementRef<HTMLInputElement>;

  private currencyOptionsCancelSubject: Subject<void> = new Subject<void>();
  uploadPreference: FuelSlipUploadPreference = {
    amountColumn: 0,
    amountTypeColumn: 0,
    assetColumn: 0,
    dateColumn: 0,
    startRow: 0,
    userId: 0,
    volumeColumn: 0,
    utcOffset: 0,
    dateTimeCulture: '',
    currencyId: null
  };
  uploadPreferenceForm: FormGroup;
  isSubmitting = false;
  timezones: ct.Timezone[] = [];
  selectedTimezone?: ct.Timezone;
  selectedDateTimeFormat = '';
  currencies: CurrencyOption[] = [];
  filteredCurrencies: CurrencyOption[] = [];
  selectedCurrency: CurrencyOption | null = null;
  currencySearchTerm: string = '';
  showCurrencyDropdown: boolean = false;
  isCurrenciesLoading: boolean = false;
  infoCostTypeMessage: string = inject(TranslocoService).translate('fuelReconciliation.uploadPreferenceModal.costTypeInfo');

  constructor(private fuelReconciliationService: FuelReconciliationService,
    private toastr: ToastrService,
    private errorService: ErrorHandlerService) {
    this.timezones = getAllTimezones();
  }

  async ngOnInit(): Promise<void> {
    await this.loadCurrenciesAsync();
    this.fuelReconciliationService.getFuelSlipUploadPreferenceAsync(new Subject<void>()).then((response) => {
      this.uploadPreference = response.data as FuelSlipUploadPreference;
      this.setDefaultCurrencyFromLocalStorage();
      this.selectedTimezone = this.timezones.find(tz => tz.utcOffset === toMinutes(this.uploadPreference.utcOffset));
      this.selectedDateTimeFormat = Object.keys(dateTimeCultures).find(key => dateTimeCultures[key] === this.uploadPreference.dateTimeCulture) ?? '';
    });
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.amount-currency-input-group')) {
      this.showCurrencyDropdown = false;
    }
  }

  private readonly modalRef = inject(NgbActiveModal);
  closeModal(): void {
    this.modalRef.close();
  }

  async submit(): Promise<void> {
    this.isSubmitting = true;
    try {
      await this.fuelReconciliationService.upsertFuelSlipUploadPreferenceAsync({
        amountColumn: this.uploadPreference.amountColumn,
        amountTypeColumn: this.uploadPreference.amountTypeColumn,
        assetColumn: this.uploadPreference.assetColumn,
        dateColumn: this.uploadPreference.dateColumn,
        startRow: this.uploadPreference.startRow,
        currencyId: this.selectedCurrency ? this.selectedCurrency.id : this.uploadPreference.currencyId,
        volumeColumn: this.uploadPreference.volumeColumn,
        utcOffset: this.selectedTimezone ? toSeconds(this.selectedTimezone.utcOffset) : this.uploadPreference.utcOffset,
        dateTimeCulture: this.selectedDateTimeFormat ? dateTimeCultures[this.selectedDateTimeFormat] : this.uploadPreference.dateTimeCulture,
      } as UpsertFuelSlipUploadPreferenceRequest, new Subject<void>());
      this.closeModal();
      this.toastr.success(this.transloco.translate('fuelReconciliation.messages.uploadPreferenceSaved'));
      this.upsertSuccess.emit();
    } catch (error: any) {
      this.errorService.handleError(error);
    } finally {
      this.isSubmitting = false;
    }
  }

  get dateTimeFormats(): string[] {
    return Object.keys(dateTimeCultures);
  }

  public async loadCurrenciesAsync(): Promise<void> {
    this.isCurrenciesLoading = true;
    try {
      const response = await this.fuelReconciliationService.getCurrencyOptionsAsync(this.currencyOptionsCancelSubject);
      this.currencies = response.data ?? [];
      this.filteredCurrencies = [...this.currencies];
      this.setDefaultCurrencyFromLocalStorage();
    } catch (error: any) {
      this.errorService.handleError(error);
    } finally {
      this.isCurrenciesLoading = false;
    }
  }

  public filterCurrencies(): void {
    if (!this.currencySearchTerm || this.currencySearchTerm.trim() === '') {
      this.filteredCurrencies = [...this.currencies];
      return;
    }

    const searchTerm = this.currencySearchTerm.toLowerCase().trim();
    this.filteredCurrencies = this.currencies.filter(
      (currency) =>
        currency.name.toLowerCase().includes(searchTerm) ||
        currency.code.toLowerCase().includes(searchTerm)
    );
  }

  public selectCurrency(currency: CurrencyOption): void {
    this.selectedCurrency = currency;
    this.showCurrencyDropdown = false;
    this.currencySearchTerm = '';
    this.filteredCurrencies = [...this.currencies];
  }

  public toggleCurrencyDropdown(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.showCurrencyDropdown = !this.showCurrencyDropdown;

    if (this.showCurrencyDropdown) {
      this.currencySearchTerm = '';
      this.filteredCurrencies = [...this.currencies];

      setTimeout(() => {
        this.currencySearchInput?.nativeElement.focus();
      }, 0);
    } else {
      this.currencySearchTerm = '';
      this.filteredCurrencies = [...this.currencies];
    }
  }

  private setDefaultCurrencyFromLocalStorage(): void {
    if (!this.currencies || this.currencies.length === 0) {
      return;
    }

    const matchingCurrency = this.currencies.find(
      c => c.id === this.uploadPreference.currencyId
    );

    if (matchingCurrency) {
      this.selectedCurrency = matchingCurrency;
      return;
    }
  }

  public ngOnDestroy(): void {
    this.currencyOptionsCancelSubject.next();
    this.currencyOptionsCancelSubject.complete();
  }
}